/*
 * Capability catalogue — the single frontend source of truth for capability
 * strings. Mirrors the backend catalogue (backend `p1-01`, design doc §3.4)
 * string-for-string: 28 capabilities, signed off with the backend
 * (17 from `p1-01`, plus `partner:view_audit` from PA-AUDIT,
 * `partner:manage_sso` from the partner-managed SSO change,
 * `content:manage_categories` from PA-CONTENT, the five initial
 * `compliance:*` capabilities from PA-COMPL, `compliance:reports` and
 * `compliance:export` from PA-COMPL Wave F, and
 * `compliance:manage_sections` from GOV-01).
 *
 * UI/route gating reads these values. They are NEVER an access decision on
 * their own — server-side enforcement (backend `p1-04`) is always authoritative.
 *
 * The catalogue is **fail-closed on unknown strings** (`isCapability`), so it
 * has to learn a new name in the same release the backend ships it — otherwise
 * a capability the server grants is invisible here and the screen it gates is
 * unreachable.
 *
 * **One list, deliberately.** There was a second, narrower `GRANTABLE_CAPABILITIES`
 * holding back `partner:view_audit` and `content:manage_categories` on the belief
 * that the role write DTO would refuse them. It whitelists `ALL_CAPABILITIES` and
 * always has (both have been in the backend enum since PA-AUDIT and PA-CONTENT), so
 * the only thing the split achieved was a roles editor that could not grant either —
 * no partner could build a role that reads the Audit Log. Anything the backend
 * stores belongs here and is offered; keeping a hand-maintained subset in step with
 * this list is exactly the drift that caused it.
 */

/** Every capability string, in catalogue order. The one place they are declared. */
export const CAPABILITIES = [
  // partner-scoped
  'partner:manage_settings',
  'partner:manage_members',
  'partner:manage_roles',
  'partner:invite',
  'partner:manage_billing',
  'partner:manage_workspaces',
  // Partner-managed SSO. Its own capability rather than folding into
  // `partner:manage_settings` so an Owner can delegate the Entra connection to
  // an IT person without also handing over billing and roles. Owner holds it by
  // way of the full-catalogue bundle; Manager deliberately does not.
  'partner:manage_sso',
  // PA-AUDIT §9.1 — read-only access to the partner Audit Log. Mirrors the
  // backend's `PartnerCapability.ViewAudit`.
  'partner:view_audit',
  // workspace-scoped
  'workspace:manage_members',
  'workspace:manage_settings',
  // content
  'content:view',
  'content:create',
  'content:edit',
  'content:publish',
  'content:approve',
  'content:delete',
  // Taxonomy authority for the Categories screen (PA-CONTENT §3). Deliberately
  // separate from `content:delete`, which is about content *items* — deleting a
  // post should not confer authority over the tree it hangs off.
  //
  // Seeded by the backend on Editor, Manager and Owner but absent here until
  // 2026-09-09, so `isCapability` rejected it and `sanitisePartnerAccess` stripped
  // it out of every session.
  'content:manage_categories',
  // compliance (PA-COMPL §3) — the compliance surface migrating out of the
  // legacy creator area. `compliance:view` is the read gate the dashboard route
  // carries; the other four are the write authorities each later wave needs, so
  // they are declared together rather than dribbled in a wave at a time.
  'compliance:view',
  'compliance:manage_bots',
  'compliance:manage_policies',
  'compliance:manage_access',
  // Deliberately separate from `content:approve`: signing off that a post meets
  // a *regulatory* rule is not the same authority as signing off its copy, and
  // the two are held by different people in every partner we have asked.
  'compliance:approve',
  // The reports seat (PA-COMPL Wave F §6.2). These two are the only capabilities
  // in the catalogue with a dependency — see `CAPABILITY_IMPLIES` below.
  // `compliance:reports` is NOT on the Editor bundle: `compliance:view` is, and
  // the reports surface (cross-user league tables, the workspace roster, caption
  // text) is an officer's, not an author's.
  'compliance:reports',
  'compliance:export',
  // Sections and documents governance (rule-governance plan D19, GOV-01). In
  // the catalogue now because splitting it out later would need a migration,
  // but **held by no seeded partner role at launch** — see
  // `LAUNCH_HIDDEN_CAPABILITIES` — and granted per partner on request. Until
  // then platform staff operate the hidden Sections/Documents screens through
  // the superAdmin partner-switcher.
  'compliance:manage_sections',
  // social
  'social:manage_connections',
  // analytics
  'analytics:view',
  'analytics:export',
] as const;

/** A single capability string from the catalogue. */
export type Capability = (typeof CAPABILITIES)[number];

/**
 * Capabilities in the catalogue that NO seeded system role receives. Mirrors
 * the backend `LAUNCH_HIDDEN_CAPABILITIES` (`capability.enum.ts`, GOV-01 / plan
 * U1): the backend Owner bundle used to be the whole catalogue, and its role
 * seeder heals drift, so a launch-hidden capability has to be subtracted on
 * both sides or every partner Owner receives it on the next heal. They stay in
 * `CAPABILITIES` so a per-partner grant validates and renders in the role editor.
 */
export const LAUNCH_HIDDEN_CAPABILITIES: readonly Capability[] = ['compliance:manage_sections'];

const CAPABILITY_SET: ReadonlySet<string> = new Set(CAPABILITIES);

/** Type guard: is an arbitrary string a known capability? Use when validating API payloads. */
export function isCapability(value: string): value is Capability {
  return CAPABILITY_SET.has(value);
}

/**
 * Capability implications: holding the key implies holding everything in its
 * value. Mirrors the backend `CAPABILITY_IMPLIES` (`capability.enum.ts`)
 * entry-for-entry — the backend applies the same closure when it resolves a
 * member's effective capabilities and when it stores a role, so a divergence
 * here shows up as a role editor that ticks a different set from the one the
 * API saves.
 *
 * **This is the FIRST and ONLY dependency in the model** (PA-COMPL Wave F §6.2,
 * RULED by Gareth 2026-09-09). Everything else is flat and independent, and a
 * shared `scope:` prefix implies nothing. In particular
 * `compliance:manage_access` alone stays a supported persona **without**
 * `compliance:view` (Wave E ruling) — it is deliberately absent here and must
 * not be added.
 */
export const CAPABILITY_IMPLIES: Partial<Record<Capability, readonly Capability[]>> = {
  'compliance:reports': ['compliance:view'],
  'compliance:export': ['compliance:reports'],
};

/**
 * Transitive closure of `caps` under {@link CAPABILITY_IMPLIES}:
 * `['compliance:export']` returns
 * `['compliance:export', 'compliance:reports', 'compliance:view']`.
 *
 * Order is stable — input order first, then implied additions in the order they
 * were discovered — and duplicates are removed. Same semantics as the backend's
 * `closeCapabilities`.
 */
export function closeCapabilities(caps: readonly Capability[]): Capability[] {
  const out: Capability[] = [];
  const seen = new Set<Capability>();
  const queue: Capability[] = [...caps];

  while (queue.length) {
    const cap = queue.shift() as Capability;
    if (seen.has(cap)) {
      continue;
    }
    seen.add(cap);
    out.push(cap);
    queue.push(...(CAPABILITY_IMPLIES[cap] ?? []));
  }

  return out;
}

/**
 * Which of `held` require `capability` — i.e. which held capabilities close onto
 * it. Empty when nothing does, which is the same thing as "safe to untick".
 *
 * The role editor uses this twice: to disable a tick box that something else is
 * keeping on, and to say *what* is keeping it on ("Required by Export"). Only
 * the capability itself is excluded from the search, so
 * `requiredBy('compliance:view', ['compliance:export'])` correctly returns
 * `['compliance:export']` through the two-step chain.
 *
 * The result is ordered by {@link CAPABILITIES} position, **not** by the order
 * of `held`. `held` is typically a `Set` built as the partner ticks boxes, so
 * iterating it would make the editor's caption tick-order dependent — the same
 * two capabilities reading "Required by Reports, Export" or "Required by
 * Export, Reports" depending on which box was clicked first, and a role reopened
 * for editing disagreeing with the one just saved.
 */
export function requiredBy(capability: Capability, held: Iterable<Capability>): Capability[] {
  const dependents: Capability[] = [];
  for (const candidate of held) {
    if (candidate === capability) {
      continue;
    }
    if (closeCapabilities([candidate]).includes(capability)) {
      dependents.push(candidate);
    }
  }
  return dependents.sort((a, b) => CAPABILITIES.indexOf(a) - CAPABILITIES.indexOf(b));
}
